import L from 'leaflet'
import { escapeHtml } from '../lib/escape.js'

function cursorIcon(name, color) {
  return L.divIcon({
    className: 'cursor-presence',
    html: `
      <svg class="cursor-arrow" width="18" height="18" viewBox="0 0 18 18">
        <path d="M2 2 L2 15 L6 11 L9 17 L11 16 L8 10 L14 10 Z"
              fill="${color}" stroke="#fff" stroke-width="1"/>
      </svg>
      <span class="cursor-name" style="background:${color}">${escapeHtml(name)}</span>`,
    iconSize: [0, 0],
    iconAnchor: [2, 2],
  })
}

// Wires the Yjs awareness channel to live cursors + a presence list. Returns a
// controller; call setEditing() to broadcast which feature you're editing and
// destroy() on teardown.
export function createPresence({ provider, map, identity, onPresenceChange }) {
  const awareness = provider.awareness
  awareness.setLocalStateField('user', { name: identity.name, color: identity.color })

  const cursorMarkers = new Map() // clientId -> { marker, key }
  let lastSent = 0

  function onMouseMove(e) {
    const now = performance.now()
    if (now - lastSent < 50) return // throttle to ~20/s
    lastSent = now
    awareness.setLocalStateField('cursor', { lat: e.latlng.lat, lng: e.latlng.lng })
  }
  function onMouseOut() {
    awareness.setLocalStateField('cursor', null)
  }
  map.on('mousemove', onMouseMove)
  map.on('mouseout', onMouseOut)

  let destroyed = false

  function render() {
    if (destroyed) return // the deferred initial render may fire after teardown
    const states = awareness.getStates()
    const list = []
    const alive = new Set()

    states.forEach((state, clientId) => {
      const user = state.user
      if (!user) return
      const isSelf = clientId === awareness.clientID
      list.push({
        clientId,
        name: user.name,
        color: user.color,
        self: isSelf,
        editing: state.editing || null,
      })
      if (isSelf) return

      const cur = state.cursor
      if (cur && typeof cur.lat === 'number') {
        alive.add(clientId)
        const key = `${user.name}|${user.color}`
        let entry = cursorMarkers.get(clientId)
        if (!entry) {
          const marker = L.marker([cur.lat, cur.lng], {
            icon: cursorIcon(user.name, user.color),
            interactive: false,
            keyboard: false,
            zIndexOffset: 10000,
          }).addTo(map)
          cursorMarkers.set(clientId, { marker, key })
        } else {
          entry.marker.setLatLng([cur.lat, cur.lng])
          if (entry.key !== key) {
            entry.marker.setIcon(cursorIcon(user.name, user.color))
            entry.key = key
          }
        }
      }
    })

    // Drop cursors for clients that left or hid their cursor.
    for (const [clientId, entry] of cursorMarkers) {
      if (!alive.has(clientId)) {
        entry.marker.remove()
        cursorMarkers.delete(clientId)
      }
    }

    list.sort((a, b) => (a.self ? -1 : b.self ? 1 : a.name.localeCompare(b.name)))
    onPresenceChange?.(list)
  }

  awareness.on('change', render)
  // Defer the first render: onPresenceChange reaches back into the session's
  // `presence` const (refreshEditingHighlights), which isn't assigned until
  // createPresence returns. A microtask runs after that assignment, avoiding a
  // temporal-dead-zone ReferenceError on mount.
  queueMicrotask(render)

  return {
    setEditing(featureId) {
      awareness.setLocalStateField('editing', featureId || null)
    },
    getEditors() {
      // map of featureId -> [{name,color}] of OTHER clients editing it (excludes
      // self, so we never highlight our own selection as "being edited").
      const editors = new Map()
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === awareness.clientID) return
        if (state.editing && state.user) {
          const arr = editors.get(state.editing) || []
          arr.push({ name: state.user.name, color: state.user.color })
          editors.set(state.editing, arr)
        }
      })
      return editors
    },
    destroy() {
      destroyed = true
      map.off('mousemove', onMouseMove)
      map.off('mouseout', onMouseOut)
      awareness.off('change', render)
      for (const [, entry] of cursorMarkers) entry.marker.remove()
      cursorMarkers.clear()
      awareness.setLocalState(null)
    },
  }
}
